import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';


// services, models, environment
import { environment } from '../../environments/environment';
import { LocalStorageService } from './local-storage.service';

@Injectable( {
  providedIn: 'root'
} )

export class CommonService
{
  constructor(
    private _http: HttpClient,
    private _localStorage: LocalStorageService,
  ) { }

  private API_BASE = environment.apiBaseUrl;
  private API_VERSION = environment.apiEndpoints.version;

  public async getAppVersion(): Promise<string | null>
  {
    if ( this._localStorage.has( 'appVersion' ) )
    {
      return this._localStorage.get<string>( 'appVersion' );
    }
    try
    {
      const response = await this._http.get<string>( this.API_BASE + this.API_VERSION ).toPromise();
      this._localStorage.set<string>( 'appVersion', response || 'Unknown Version' );
    }
    catch ( error )
    {
      this._localStorage.set<string>( 'appVersion', 'Unknown Version' );
      console.error( 'Error fetching app version:', error );
    }
    finally
    {
      return this._localStorage.get<string>( 'appVersion' );
    }
  }

  public FileHelper = {

    async getFileExtension( file: File ): Promise<string | undefined>
    {
      let extension: string | undefined;
      const parts = file.name.toLowerCase().split( '.' );
      extension = parts[parts.length - 1];
      return extension;
    },

    async convertToCsv( data: any[] ): Promise<string>
    {
      if ( !data || data.length === 0 ) return '';
      const headers = Object.keys( data[0] ).join( ',' );
      const csvRows = data.map( obj => Object.values( obj ).map( value => `"${value}"` ).join( "," ) );
      return [headers, ...csvRows].join( '\n' );
    },

    async downloadFile( content: string, filename: string, mimeType: string ): Promise<void>
    {
      const blob = new Blob( [content], { type: mimeType } );
      const url = window.URL.createObjectURL( blob );
      const a = document.createElement( 'a' );
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL( url );
    }

  }

}

// common interfaces for table columns
export interface TableColumn
{
  label: string;
  field?: string;
  type?: string;
  link?: string;
  format?: string;
  sortable?: boolean;
  filterable?: boolean;
  filterType?: string;
  total?: boolean;
  totalType?: 'sum' | 'count' | 'avg';
  editable?: boolean;
}

export interface TableColumns
{
  DefaultColumns: TableColumn[];
  HiddenColumns?: TableColumn[];
}

export interface IPagedData
{
  total: number;
  data: any[];
}

export interface IPaginatorOptions
{
  pageSizeOptions: number[];
  pageIndex: number;
  length: number;
}

export const FormTypeTableColumns: Record<string, TableColumns> = {
  P: {
    DefaultColumns: [
      { label: 'Pathway', field: 'formName', type: 'link', sortable: true, filterable: true, filterType: 'select' },
      { label: 'client.forms.pathways.columns.start-date', field: 'startDate', type: 'date', sortable: true },
      { label: 'client.forms.pathways.columns.service', field: 'formSubType', sortable: true },
      { label: 'client.forms.pathways.columns.last-updated', field: 'lastUpdatedDate', type: 'date', sortable: true },
      { label: 'client.forms.pathways.columns.last-signed', field: 'lastSignedDate', type: 'date', sortable: true },
    ]
  },
  C: {
    DefaultColumns: [
      { label: 'Checklist', field: 'formName', type: 'link', sortable: true },
      { label: 'client.forms.checklists.columns.visit-date', field: 'startDate', type: 'date', sortable: true },
      { label: 'client.forms.checklists.columns.created-date', field: 'createdDate', type: 'date', sortable: true },
      { label: 'client.forms.checklists.columns.updated-date', field: 'lastUpdatedDate', type: 'date', sortable: true },
      { label: 'client.forms.checklists.columns.last-signed', field: 'lastSignedDate', type: 'date', sortable: true },
    ]
  },
  T: {
    DefaultColumns: [
      { label: 'Tool', field: 'formName', type: 'link', sortable: true },
      { label: 'client.forms.pathways.columns.start-date', field: 'startDate', type: 'date', sortable: true },
      { label: 'client.forms.pathways.columns.last-signed', field: 'lastSignedDate', type: 'date', sortable: true },
      { label: 'Status' },
      { label: 'Score' },
      { label: 'Call Reason' },
      { label: 'Referral Source' },
    ]
  },
  'T-HC': {
    DefaultColumns: [
      { label: 'Healthy Change Tool', field: 'formName', type: 'link', sortable: true },
      { label: 'Start Date', field: 'startDate', type: 'date', sortable: true },
      { label: 'Last Signed', field: 'lastSignedDate', type: 'date', sortable: true },
      { label: 'Status' },
      { label: 'Score' },
      { label: 'Call Reason' },
      { label: 'Referral Source' },
    ]
  },
  Contact: {
    DefaultColumns: [
      { label: 'caseload.tabs.contacts.columns.day', field: 'creationDate', type: 'date', format: 'EEE, MMM dd yyyy', sortable: true },
      { label: 'caseload.tabs.contacts.columns.start', field: 'eventStartDateTim', type: 'date', format: 'hh:mm a', sortable: true },
      { label: 'caseload.tabs.contacts.columns.end', field: 'eventEndDateTim', type: 'date', format: 'hh:mm a', sortable: true },
      { label: 'caseload.tabs.contacts.columns.duration', field: 'totaltime', sortable: true },
      { label: 'caseload.tabs.contacts.columns.type', field: 'visitType', sortable: true },
      { label: 'caseload.tabs.contacts.columns.reason', field: 'contactReason' },
      { label: 'caseload.tabs.contacts.columns.goal', field: 'goal' },
      { label: 'caseload.tabs.contacts.columns.outcome', field: 'outcome', sortable: true },
      { label: 'caseload.tabs.contacts.columns.created-by', field: 'recoredupdateduserid', sortable: true },
      { label: '', field: 'eventId', type: 'link', link: 'client/contact/' }
    ]
  },
  Tasks: {
    DefaultColumns: [
      { label: 'Task', field: 'taskText', sortable: true },
      { label: 'Priority', field: 'taskLabel', sortable: true },
      { label: 'Form', field: 'formName', type: 'link', link: '', sortable: true },
      { label: 'Start Date', field: 'insertedDate', type: 'date', sortable: true },
    ]
  },
}

export const CaseloadTableColumns: Record<string, TableColumns> = {
  Default: {
    DefaultColumns: [
      { label: 'caseload.tabs.caseload.columns.client-id', field: 'clientId', type: 'link', sortable: true, filterable: true, filterType: 'text' },
      { label: 'caseload.tabs.caseload.columns.first-name', field: 'firstName', sortable: true, filterable: true, filterType: 'text' },
      { label: 'caseload.tabs.caseload.columns.last-name', field: 'lastName', sortable: true, filterable: true, filterType: 'text' },
      { label: 'caseload.tabs.caseload.columns.date-of-birth', field: 'dateOfBirthString', type: 'date', sortable: true },
      { label: 'caseload.tabs.caseload.columns.status', field: 'activeStatus', sortable: true, filterable: true, filterType: 'select' },
    ],
    HiddenColumns: [
      { label: 'Middle Name', field: 'middleName', sortable: true, filterable: true, filterType: 'text' },
      { label: 'Suffix', field: 'nameSuffix', sortable: true, filterable: true, filterType: 'text' },
    ]
  },
  Supervisor: {
    DefaultColumns: [
      { label: 'Client ID', field: 'clientId', type: 'link', sortable: true, filterable: true, filterType: 'text' },
      { label: 'First Name', field: 'firstName', sortable: true, filterable: true, filterType: 'text' },
      { label: 'Last Name', field: 'lastName', sortable: true, filterable: true, filterType: 'text' },
      { label: 'Date of Birth', field: 'dateOfBirthString', type: 'date', sortable: true },
      { label: 'Active Status', field: 'activeStatus', sortable: true, filterable: true, filterType: 'select' },
    ],
    HiddenColumns: [
      { label: 'Middle Name', field: 'middleName', sortable: true, filterable: true, filterType: 'text' },
      { label: 'Suffix', field: 'nameSuffix', sortable: true, filterable: true, filterType: 'text' },
    ]
  },
  HubManager: {
    DefaultColumns: [
      { label: 'Client ID', field: 'clientId', type: 'link', sortable: true, filterable: true, filterType: 'text' },
      { label: 'First Name', field: 'firstName', sortable: true, filterable: true, filterType: 'text' },
      { label: 'Last Name', field: 'lastName', sortable: true, filterable: true, filterType: 'text' },
      { label: 'Date of Birth', field: 'dateOfBirthString', type: 'date', sortable: true },
      { label: 'Active Status', field: 'activeStatus', sortable: true, filterable: true, filterType: 'select' },
    ],
    HiddenColumns: [
      { label: 'Middle Name', field: 'middleName', sortable: true, filterable: true, filterType: 'text' },
      { label: 'Suffix', field: 'nameSuffix', sortable: true, filterable: true, filterType: 'text' },
    ]
  }
}

export const ContactEventTableColumns: TableColumns = {
  DefaultColumns: [
    { label: 'caseload.tabs.contacts.columns.day', field: 'creationDate', type: 'date', format: 'EEE, MMM dd yyyy', sortable: true },
    { label: 'caseload.tabs.contacts.columns.start', field: 'eventStartDateTim', type: 'date', format: 'hh:mm a', sortable: true },
    { label: 'caseload.tabs.contacts.columns.end', field: 'eventEndDateTim', type: 'date', format: 'hh:mm a', sortable: true },
    { label: 'caseload.tabs.contacts.columns.duration', field: 'totaltime', sortable: true },
    { label: 'caseload.tabs.contacts.columns.client', field: 'clientname', sortable: true },
    { label: 'caseload.tabs.contacts.columns.type', field: 'visitType', sortable: true },
    { label: 'caseload.tabs.contacts.columns.reason', field: 'contactReason' },
    { label: 'caseload.tabs.contacts.columns.goal', field: 'goal' },
    { label: 'caseload.tabs.contacts.columns.outcome', field: 'outcome', sortable: true },
    { label: 'caseload.tabs.contacts.columns.created-by', field: 'recoredupdateduserid', sortable: true },
    { label: '', field: 'eventId', type: 'link', link: 'client/contact/' }
  ]
}

export const generateObjectHash = ( obj: any ): string =>
{
  const jsonString = JSON.stringify( obj );
  let hash = 0;
  for ( let i = 0; i < jsonString.length; i++ )
  {
    const char = jsonString.charCodeAt( i );
    hash = ( ( hash << 5 ) - hash ) + char;
    hash |= 0; // Convert to 32bit integer
  }
  return hash.toString();
}

export const GuideClaimColumns: Record<string, TableColumns> = {
  Invoice: {
    DefaultColumns: [
      { label: "", field: 'id', type: 'checkbox', sortable: false, total: true, totalType: 'count' },
      { label: 'Completed HubId', field: 'completedHubid', sortable: true, },
      { label: 'Completed AgencyID', field: 'completedAgencyid', sortable: true, },
      { label: 'HUBID', field: 'hubid', sortable: true, filterable: true, filterType: 'text' },
      { label: 'AGENCYID', field: 'agencyid', sortable: true, filterable: true, filterType: 'text' },
      { label: 'Client ID', field: 'clientId', type: 'link', sortable: true, },
      { label: 'MBI', field: 'cmsMbi', sortable: true, filterable: true, filterType: 'text' },
      { label: 'billing.guide-claims.invoice-columns.client', field: 'fullname', sortable: true },
      { label: 'billing.guide-claims.invoice-columns.date-of-birth', field: 'dob', type: 'date', sortable: true },
      { label: 'billing.guide-claims.invoice-columns.account-number', field: 'accountNumber', sortable: true },
      { label: 'billing.guide-claims.invoice-columns.service-date', field: 'serviceDate', type: 'date', sortable: true },
      { label: 'billing.guide-claims.invoice-columns.event-type', field: 'eventType', sortable: true },
      { label: 'G-Code', field: 'chargeCode', sortable: true, filterable: true, filterType: 'text' },
      { label: 'billing.guide-claims.invoice-columns.activity-step', field: 'activityStep', sortable: true, filterable: true, filterType: 'text' },
      { label: 'billing.guide-claims.invoice-columns.status', field: 'claimStatus', sortable: true },
      { label: 'billing.guide-claims.invoice-columns.eft-trace-number', field: 'cmsPayeeEftTraceNbr', sortable: true },
      { label: 'billing.guide-claims.invoice-columns.claim-payment', field: 'cmsClaimPaymentAmount', type: 'currency', sortable: true },
      { label: 'billing.guide-claims.invoice-columns.eft-payment', field: 'cmsEftPaymentAmount', type: 'currency', sortable: true },
      { label: 'billing.guide-claims.invoice-columns.eft-payment-date', field: 'cmsEftPaymentDate', type: 'date', sortable: true },
      { label: 'billing.guide-claims.invoice-columns.due-payment', field: 'ccsInvoiceDueAmt', type: 'currency', sortable: true, total: true, totalType: 'sum' },
    ]
  },
  Received: {
    DefaultColumns: [
      { label: "", field: 'id', type: 'checkbox', sortable: false, total: true, totalType: 'count' },
      { label: 'Completed HubId', field: 'completedHubid', sortable: true, },
      { label: 'Completed AgencyID', field: 'completedAgencyid', sortable: true, },
      { label: 'Client ID', field: 'clientId', type: 'link', sortable: true, },
      { label: 'MBI', field: 'cmsMbi', sortable: true, filterable: true, filterType: 'text' },
      { label: 'billing.guide-claims.received-columns.client', field: 'fullname', sortable: true },
      { label: 'billing.guide-claims.received-columns.date-of-birth', field: 'dob', type: 'date', sortable: true },
      { label: 'billing.guide-claims.received-columns.account-number', field: 'accountNumber', sortable: true },
      { label: 'billing.guide-claims.received-columns.service-date', field: 'serviceDate', type: 'date', sortable: true },
      { label: 'billing.guide-claims.received-columns.event-type', field: 'eventType', sortable: true },
      { label: 'G-Code', field: 'chargeCode', sortable: true, filterable: true, filterType: 'text' },
      { label: 'billing.guide-claims.received-columns.activity-step', field: 'activityStep', sortable: true, filterable: true, filterType: 'text' },
      { label: 'billing.guide-claims.received-columns.status', field: 'claimStatus', sortable: true },
      { label: 'billing.guide-claims.received-columns.eft-trace-number', field: 'cmsPayeeEftTraceNbr', sortable: true },
      { label: 'billing.guide-claims.received-columns.claim-payment', field: 'cmsClaimPaymentAmount', type: 'currency', sortable: true },
      { label: 'billing.guide-claims.received-columns.eft-payment', field: 'cmsEftPaymentAmount', type: 'currency', sortable: true  },
      { label: 'billing.guide-claims.received-columns.eft-payment-date', field: 'cmsEftPaymentDate', type: 'date', sortable: true },      
      { label: 'billing.guide-claims.received-columns.gp-invoice-date', field: 'gpInvoicedDate', type: 'date', sortable: true },
      { label: 'billing.guide-claims.received-columns.gp-invoice-number', field: 'gpInvoiceNumber', sortable: true },
      { label: 'billing.guide-claims.received-columns.due-payment', field: 'ccsInvoiceDueAmt', type: 'currency', sortable: true },
      { label: 'billing.guide-claims.received-columns.received-amount', field: 'receivedAmount', type: 'currency', total: true, totalType: 'sum', sortable: true, editable: true },
    ]
  },
  Remitted: {
    DefaultColumns: [
      { label: "", field: 'id', type: 'checkbox', sortable: false, total: true, totalType: 'count' },
      { label: 'Completed HubId', field: 'completedHubid', sortable: true, },
      { label: 'Completed AgencyID', field: 'completedAgencyid', sortable: true, },
      { label: 'Agency', field: 'agencyName', sortable: true, filterable: true, filterType: 'text' },
      { label: 'Client ID', field: 'ccsClientId', type: 'link', sortable: true, },
      { label: 'MBI', field: 'cmsMbi', sortable: true, filterable: true, filterType: 'text' },
      { label: 'billing.guide-claims.remmitted-columns.client', field: 'fullname', sortable: true },
      { label: 'billing.guide-claims.remmitted-columns.date-of-birth', field: 'dob', type: 'date', sortable: true },
      { label: 'billing.guide-claims.remmitted-columns.alignment-status', field: 'alignmentStatus', sortable: true, filterable: true, filterType: 'text' },
      { label: 'billing.guide-claims.remmitted-columns.alignment-date', field: 'alignmentDate', type: 'date', sortable: true },
      { label: 'billing.guide-claims.remmitted-columns.alignment-start-date', field: 'initialAlignmentStartDate', type: 'date', sortable: true },
      { label: 'billing.guide-claims.remmitted-columns.gcn', field: 'gcn', sortable: true },
      { label: 'billing.guide-claims.remmitted-columns.gcn-name', field: 'gcnName', sortable: true },
      { label: 'billing.guide-claims.remmitted-columns.account-number', field: 'accountNumber', sortable: true },
      { label: 'billing.guide-claims.remmitted-columns.service-date', field: 'serviceDate', type: 'date', sortable: true },
      { label: 'billing.guide-claims.remmitted-columns.event-type', field: 'eventType', sortable: true },
      { label: 'G-Code', field: 'chargeCode', sortable: true, filterable: true, filterType: 'text' },
      { label: 'billing.guide-claims.remmitted-columns.activity-step', field: 'activityStep', sortable: true, filterable: true, filterType: 'text' },
      { label: 'billing.guide-claims.remmitted-columns.submitted-date', field: 'submittedDate', type: 'date', sortable: true },
      { label: 'billing.guide-claims.remmitted-columns.status', field: 'claimStatus', sortable: true },
      { label: 'billing.guide-claims.remmitted-columns.eft-payment-date', field: 'cmsEftDate', type: 'date', sortable: true },
      { label: 'billing.guide-claims.remmitted-columns.received-date', field: 'ccsReceivedDate', type: 'date', sortable: true },
      { label: 'billing.guide-claims.remmitted-columns.received-amount', field: 'ccsReceivedAmount', type: 'currency', total: true, totalType: 'sum', sortable: true, editable: true },
      { label: 'billing.guide-claims.remmitted-columns.hub-invoice-number', field: 'hubInvoiceNumber', sortable: true },
      { label: 'billing.guide-claims.remmitted-columns.hub-invoice-date', field: 'hubInvoiceDate', type: 'date', sortable: true },
      { label: 'billing.guide-claims.remmitted-columns.hub-invoice-amount', field: 'hubInvoiceAmt', type: 'currency', sortable: true },
      { label: 'billing.guide-claims.remmitted-columns.sub-agency-amount', field: 'subAgencyAmt', type: 'currency', sortable: true },
    ]
  }
}
